import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, RefreshCw, Search, Play, FileText, HelpCircle, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCurriculum() {
  const { user, status: authStatus } = useAuth();
  const [state, setState] = useState({ loading: true, error: null });
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [module, setModule] = useState("all");
  const [videoOnly, setVideoOnly] = useState(false);

  const load = () => {
    setState({ loading: true, error: null });
    axios.get(`${API}/admin/curriculum`)
      .then(({ data }) => { setRows(data.lessons || []); setState({ loading: false, error: null }); })
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Could not load curriculum." }));
  };

  useEffect(() => { if (authStatus === "authed" && user?.role === "admin") load(); }, [authStatus, user]);

  const moduleOptions = useMemo(() => {
    const seen = new Set(); const opts = [];
    for (const r of rows) if (!seen.has(r.module_slug)) { seen.add(r.module_slug); opts.push({ slug: r.module_slug, label: `${r.module_number}. ${r.module_title}` }); }
    return opts;
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (module !== "all" && r.module_slug !== module) return false;
      if (videoOnly && !r.has_video) return false;
      if (needle) {
        const hay = `${r.lesson_title} ${r.module_title} ${r.lesson_slug}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, q, module, videoOnly]);

  const stats = useMemo(() => ({
    total: rows.length,
    withVideo: rows.filter((r) => r.has_video).length,
    withQuiz: rows.filter((r) => r.questions > 0).length,
  }), [rows]);

  if (authStatus === "checking") return <div className="min-h-[60vh] flex items-center justify-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking session…</div>;
  if (authStatus !== "authed") return <Navigate to="/login" replace />;
  if (user?.role !== "admin") return <Navigate to="/member/hub" replace />;

  return (
    <div data-testid="admin-curriculum-page" className="bg-slate-50 min-h-screen py-14 px-6 lg:px-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-start justify-between mb-8 gap-4">
          <div>
            <div className="eyebrow text-brand-orange mb-2">Admin</div>
            <h1 className="font-serif text-4xl lg:text-5xl text-brand-blue leading-tight">Curriculum preview.</h1>
            <p className="text-slate-600 mt-3 max-w-2xl">Every module and lesson in the Toolbox. Click Preview to open a lesson exactly as a paying practitioner sees it — check the video, documents and quiz before you announce anything.</p>
          </div>
          <button data-testid="curriculum-refresh-btn" onClick={load} className="btn-secondary whitespace-nowrap"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div data-testid="stat-total-lessons" className="bg-white border border-slate-200 p-6"><div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500"><FileText className="w-3.5 h-3.5" /> Total lessons</div><div className="mt-3 font-serif text-3xl text-brand-blue">{stats.total}</div></div>
          <div className="bg-white border border-slate-200 p-6"><div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500"><Play className="w-3.5 h-3.5" /> Lessons with video</div><div className="mt-3 font-serif text-3xl text-brand-blue">{stats.withVideo}<span className="text-base text-slate-400 ml-2">of {stats.total}</span></div></div>
          <div className="bg-white border border-slate-200 p-6"><div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500"><HelpCircle className="w-3.5 h-3.5" /> Lessons with quiz</div><div className="mt-3 font-serif text-3xl text-brand-blue">{stats.withQuiz}<span className="text-base text-slate-400 ml-2">of {stats.total}</span></div></div>
        </div>

        <div className="bg-white border border-slate-200 p-4 mb-6 flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="curriculum-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lesson or module title…" className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 focus:outline-none focus:border-brand-blue" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Module</label>
            <select data-testid="curriculum-filter-module" value={module} onChange={(e) => setModule(e.target.value)} className="text-sm border border-slate-300 px-2 py-2 focus:outline-none focus:border-brand-blue min-w-[220px]">
              <option value="all">All modules</option>
              {moduleOptions.map((m) => <option key={m.slug} value={m.slug}>{m.label}</option>)}
            </select>
          </div>
          <label className="text-xs uppercase tracking-widest font-semibold text-slate-500 inline-flex items-center gap-2 py-2">
            <input type="checkbox" data-testid="curriculum-video-only" checked={videoOnly} onChange={(e) => setVideoOnly(e.target.checked)} /> Video only
          </label>
        </div>

        {state.error && <div className="mb-8 border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-900">{state.error}</div>}
        {state.loading ? <div className="py-24 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading curriculum…</div> : (
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest font-semibold text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-3">Module</th><th className="px-4 py-3">Lesson</th>
                  <th className="px-4 py-3">Video</th><th className="px-4 py-3">Quiz</th>
                  <th className="px-4 py-3 text-right">Preview</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={`${r.module_slug}/${r.lesson_slug}`} data-testid={`curriculum-row-${r.lesson_slug}`} className="border-b border-slate-100 hover:bg-slate-50/50 align-top">
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">M{r.module_number} · {r.module_title}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{r.lesson_title}</div>
                      {r.description && <div className="text-xs text-slate-500 mt-1 max-w-[520px]">{r.description}</div>}
                    </td>
                    <td className="px-4 py-3">{r.has_video ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3 h-3" /> {r.video_id}</span> : <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertCircle className="w-3 h-3" /> missing</span>}</td>
                    <td className="px-4 py-3 text-xs text-slate-700">{r.questions > 0 ? `${r.questions} Q` : <span className="text-amber-700">-</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        data-testid={`curriculum-preview-${r.lesson_slug}`}
                        to={`/member/toolbox/${r.module_slug}/${r.lesson_slug}`}
                        target="_blank"
                        rel="noopener"
                        className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold text-brand-orange hover:text-brand-blue"
                      >
                        <ExternalLink className="w-3 h-3" /> Preview lesson
                      </Link>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-sm text-slate-500">No lessons match your filters.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
